import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { PageHero } from "@/components/marketing/PageHero";
import { Btn, Eyebrow, SectionHeader } from "@/components/marketing/primitives";
import { ContactCTA } from "@/components/marketing/ContactCTA";
import { PartnerBadges } from "@/components/marketing/PartnerBadges";
import { SERVICES } from "@/content/services";
import { SERVICE_SECTIONS } from "@/content/serviceSections";
import { FAQSection } from "@/components/marketing/FAQSection";
import { CrossLinks } from "@/components/marketing/CrossLinks";
import { RelatedLinks } from "@/components/marketing/RelatedLinks";
import { PostLinkLine } from "@/components/marketing/PostLinkLine";
import { SERVICE_POST_LINKS, SERVICE_EXTRA_POST_LINKS } from "@/content/postlinks";
import { SERVICE_INDUSTRY_LINKS } from "@/content/crosslinks";
import { pushAuditStart } from "@/lib/analytics";

import { SERVICE_FAQS, faqJsonLd } from "@/content/faqs";
import { breadcrumbJsonLd } from "@/content/schema";
import { OG_IMAGE, ORG_ID, abs } from "@/content/siteConfig";
import { SectionLayout } from "@/components/marketing/SectionLayout";
import { ServiceDiagram } from "@/components/marketing/ServiceDiagram";
import { RichProse } from "@/components/marketing/RichProse";
import { serviceWhoSection } from "@/content/whoRuns";


export const Route = createFileRoute("/services/$service")({
  loader: ({ params }) => {
    const svc = SERVICES.find((s) => s.slug === params.service);
    if (!svc) throw notFound();
    return svc;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [] };
    const title = loaderData.slug === "local-services-ads"
      ? "Local Services Ads Management for Arizona Contractors | BHG"
      :
      loaderData.seoTitle ??
      `${loaderData.title.replace(/-\s*$/, "").trim()}${loaderData.italicSuffix ? " " + loaderData.italicSuffix : ""}, Berk Hospitality Group`;
    const description = loaderData.seoDescription ?? loaderData.description;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: OG_IMAGE },
        { name: "twitter:image", content: OG_IMAGE },
        { property: "og:url", content: abs(`/services/${loaderData.slug}`) },
      ],
      links: [
        { rel: "canonical", href: abs(`/services/${loaderData.slug}`) },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            {
              name: loaderData.title.replace(/-\s*$/, "").trim(),
              path: `/services/${loaderData.slug}`,
            },
          ]),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: loaderData.slug === "seo-ai-search" ? "SEO & AI Search" : loaderData.slug === "local-services-ads" ? loaderData.seoTitle : title,
            provider: { "@id": ORG_ID },
            description: loaderData.slug === "meta-ads" ? description : loaderData.description,
            areaServed: "United States",
          }),
        },
        ...(SERVICE_FAQS[loaderData.slug]
          ? [
              {
                type: "application/ld+json",
                children: faqJsonLd(SERVICE_FAQS[loaderData.slug]!),
              },
            ]
          : []),
      ],
    };
  },
  component: ServiceDetail,
  notFoundComponent: () => (
    <SiteShell>
      <PageHero eyebrow="Service" lede="That service doesn't exist.">
        Not <span className="display-italic">found.</span>
      </PageHero>
    </SiteShell>
  ),
});

const FAQ_TITLES: Record<string, string> = {
  "google-ads": "Questions about running Google Ads.",
  "local-services-ads": "Questions about Local Services Ads.",
  "meta-ads": "Questions about Facebook and Instagram ads.",
  "youtube-ads": "Questions about YouTube ads.",
  "seo-ai-search": "Questions about SEO and AI search.",
};

const SEO_RELATED = [
  { href: "/audit", label: "Free conversion tracking audit" },
  { href: "/services/google-ads", label: "Google Ads" },
  { href: "/services/local-services-ads", label: "Local Services Ads" },
  { href: "/glossary", label: "Paid media glossary" },
  { href: "/research/conversion-tracking-report", label: "The Conversion Tracking Report" },
];

/** Renders [label](/path) markers inside section prose as links. */
function renderRich(text: string) {
  const parts: React.ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  const style = { color: "#002FA7", textDecoration: "underline", textUnderlineOffset: 3 };
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[3]) {
      parts.push(<strong key={parts.length}>{m[3]}</strong>);
      last = m.index + m[0].length;
      continue;
    }
    const label = m[1]!;
    const path = m[2]!;
    parts.push(
      <a key={parts.length} href={path} style={style}>
        {label}
      </a>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function ServiceDetail() {
  const svc = Route.useLoaderData();
  const sections = SERVICE_SECTIONS[svc.slug];
  return (
    <SiteShell>
      <PageHero
        eyebrow={`Service · ${svc.idx}`}
        lede={svc.description}
        ctas={
          <>
            <Btn to="/audit" onClick={() => pushAuditStart("inline")}>
              Get your free audit →
            </Btn>
            <Btn to="/services" variant="ghost">
              All services
            </Btn>
          </>
        }

      >
        {svc.slug === "local-services-ads" ? <>Local Services Ads management <span className="display-italic">for contractors in Arizona.</span></> : <>{svc.title}{" "}
        {svc.italicSuffix ? <span className="display-italic">{svc.italicSuffix}</span> : null}</>}
      </PageHero>

      {serviceWhoSection(svc.slug) ? <RichProse sections={[serviceWhoSection(svc.slug)!]} /> : null}

      <section className="bhg-section">
        <div className="wrap section-eyebrow-only">
          <div className="eyebrow-lead">
            <Eyebrow>What it is</Eyebrow>
            <p
              className="prose-lead"
              style={{
                fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                fontWeight: 300,
                fontSize: "clamp(20px, 2.2vw, 28px)",
                lineHeight: 1.35,
                color: "#002FA7",
                margin: 0,
                maxWidth: "52ch",
              }}
            >
              {svc.whatItIs}
            </p>
          </div>
          <div className="mt-8">
            {svc.slug === "google-ads" ? (
              <>
                <p>{renderRich("Most accounts we inherit have a measurement problem before a bidding problem, which is why [Google Ads conversions stop reporting](/blog/why-did-my-google-ads-conversions-stop) far more often than campaigns stop working. We run them from Scottsdale, for the Phoenix metro and nationwide.")}</p>
                <p>{renderRich("Not sure your tags fire? Run the [free conversion tracking checker](/audit).")}</p>
              </>
            ) : svc.slug === "seo-ai-search" ? (
              <p>{renderRich("Most contractor sites are a home page, a services page, a contact form and a blog nobody reads. We build the site out instead: a page for every question customers ask, a page for every job and area you cover, and a glossary of the terms people look up, all linked together. Google can send people to each one, and AI tools read the same pages.")}</p>
            ) : (svc.slug === "meta-ads" ? <><p>{renderRich("Offer must match the job: see [Meta ad strategies for recurring service versus one-off repair leads](/blog/meta-ad-strategies-for-pool-cleaning-vs-repair-leads), and for subscriptions, [90-day CAC and creative strategy for gym membership Meta ads](/blog/gym-membership-meta-ads-90-day-cac-and-creative-strategy), judged on retained members.")}</p><p>{renderRich("With the pixel and Conversions API live, check deduplication first: [why Meta reports more purchases than your store made](/blog/why-does-meta-report-more-purchases-than-my-store).")}</p></> : svc.slug === "local-services-ads" ? <><p>{renderRich("See [integrating LSA with Meta and Google Ads](/blog/local-f-b-paid-media-integrating-lsa-meta-and-google-ads) to avoid cannibalization, and [how Local Services Ads lead credits used to work](/blog/mastering-lsa-disputes-recover-20-of-monthly-ad-spend) for the older credit process.")}</p><p>{renderRich("Position follows reviews and response time: [the review acceleration playbook for boosting LSA rank](/blog/boost-lsa-rank-the-review-acceleration-playbook-for-home-services).")}</p></> : <><p>{renderRich("It earns a place once capture channels saturate (see [allocating a paid media budget under $50K across Meta and Google](/blog/meta-vs-google-ads-dtc-budget-allocation-under-50k)), and format is a funnel decision: [YouTube ad formats for each stage of the buyer journey](/blog/youtube-ad-formats-for-each-stage-of-the-buyer-journey).")}</p></>)}
          </div>
        </div>
      </section>

      <ServiceDiagram slug={svc.slug} />

      {sections ? (
        sections.map((s) => (
          <section className="bhg-section" key={s.num}>
            <SectionLayout>
              <SectionHeader
                num={s.num}
                title={
                  <>
                    {s.heading}{" "}
                    {s.italic ? <span className="display-italic">{s.italic}</span> : null}
                  </>
                }
              />
              <div
                className="mt-8 max-w-3xl"
                style={{ color: "rgba(0,47,167,0.74)", fontSize: 16.5, lineHeight: 1.65 }}
              >
                {s.body.map((p, i) => (
                  <p key={i} className={i === 0 ? "" : "mt-5"} style={{ margin: 0 }}>
                    {renderRich(p)}
                  </p>
                ))}
                {s.bullets ? (
                  <ul className="mt-6 list-none p-0">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        style={{
                          padding: "14px 0",
                          borderBottom: "1px solid rgba(0,47,167,0.12)",
                          display: "grid",
                          gridTemplateColumns: "20px 1fr",
                          gap: 16,
                          alignItems: "baseline",
                          fontSize: 15.5,
                        }}
                      >
                        <span className="mono" style={{ color: "#8a6f3f", fontSize: 14 }}>
                          ·
                        </span>
                        <span>{renderRich(b)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </SectionLayout>
          </section>
        ))
      ) : (
        <>
      <section className="bhg-section">
        <div className="wrap">
          <SectionHeader num="How we run it" title={<>The <span className="display-italic">approach.</span></>} />
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {svc.howWeRunIt.map((step: { title: string; body: string }, i: number) => (
              <div
                key={i}
                style={{
                  padding: "28px",
                  background: "#fff",
                  border: "1px solid rgba(0,47,167,0.12)",
                  borderRadius: 4,
                }}
              >
                <div
                  className="mono mb-4"
                  style={{
                    fontSize: 11,
                    letterSpacing: "0.14em",
                    color: "rgba(0,47,167,0.45)",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3
                  style={{
                    fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
                    fontWeight: 300,
                    fontSize: 22,
                    letterSpacing: "-0.01em",
                    margin: 0,
                    color: "#002FA7",
                  }}
                >
                  {step.title}
                </h3>
                <p
                  className="prose-small mt-3"
                  style={{ color: "rgba(0,47,167,0.74)", fontSize: 14.5, lineHeight: 1.55 }}
                >
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bhg-section">
        <div className="wrap">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <SectionHeader num="Deliverables" title={<>What you <span className="display-italic">get.</span></>} />
              <ul
                className="mt-10 list-none p-0"
              >
                {svc.deliverables.map((d: string) => (
                  <li
                    key={d}
                    style={{
                      padding: "18px 0",
                      borderBottom: "1px solid rgba(0,47,167,0.12)",
                      display: "grid",
                      gridTemplateColumns: "20px 1fr",
                      gap: 18,
                      alignItems: "baseline",
                      color: "#002FA7",
                    }}
                  >
                    <span className="mono" style={{ color: "#8a6f3f", fontSize: 14 }}>
                      ✓
                    </span>
                    <span style={{ fontSize: 15.5 }}>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <SectionHeader num="Industries" title={<>Built for <span className="display-italic">operators who scale.</span></>} />
              <ul className="mt-8 list-none p-0">
                {svc.industriesFit.map((i: string) => (
                  <li
                    key={i}
                    className="mono"
                    style={{
                      padding: "14px 0",
                      borderBottom: "1px solid rgba(0,47,167,0.12)",
                      fontSize: 12.5,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "rgba(0,47,167,0.74)",
                    }}
                  >
                    {i}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CrossLinks
        items={SERVICE_INDUSTRY_LINKS[svc.slug] ?? []}
        num="Industries we run this for"
        title={<>Where this <span className="display-italic">works hardest.</span></>}
      />
        </>
      )}

      {SERVICE_FAQS[svc.slug] ? (

        <FAQSection
          items={SERVICE_FAQS[svc.slug]!}
          num="FAQ"
          title={
            sections ? (
              <>{FAQ_TITLES[svc.slug] ?? "Common questions."}</>
            ) : (
              <>Questions we <span className="display-italic">get asked.</span></>
            )
          }
        />
      ) : null}

      <PartnerBadges />

      <RelatedLinks
        items={svc.slug === "seo-ai-search" ? SEO_RELATED : SERVICES.filter((s) => s.slug !== svc.slug && s.slug !== "seo-ai-search").map((s) => ({
          href: `/services/${s.slug}`,
          label: `${s.title.replace(/-\s*$/, "").trim()}${s.italicSuffix ? " " + s.italicSuffix : ""}`,
        }))}
      />

      <ContactCTA />
    </SiteShell>
  );
}
